import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, Formik } from 'formik';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { getAllMyFoldersAPI } from '../../../utils/apiServices/myFoldersAPI';
import { setSelectedSafetyMeetings } from '../../../redux';
import { getLocalStorageItem, popup } from '../../../utils';
import FormSelect from '../../shared/Forms/FormSelect';
import { LOCAL_STORAGE, QUERY_KEYS } from '../../../constants';
import { updateMyDocumentsAPI } from '../../../utils/apiServices/myDocumentsAPI';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import { CircleSpinner } from '../../../assets/svg';

export const RestoreDocumentModal = ({ showModal, setShowModal }) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const selectedSafetyMeetings = useSelector((s) => s.safetyMeetings.selectedSafetyMeetings);
    const accountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);

    const [debounceSearch] = useDebouncedValue(search, 500);
    const {
        data: folders,
        isFetching,
        isError,
        error,
    } = useQuery({
        queryKey: [QUERY_KEYS.FOLDERS, accountId, debounceSearch,'100', 'select'],
        queryFn: async () =>
            await getAllMyFoldersAPI({
                account_id: accountId,
                search: debounceSearch,
                limit: 100,
            }),
        select: (data) =>
            data?.data?.map((folder) => ({
                value: folder.id,
                label: folder.name,
            })),
        enabled: !!accountId && showModal,
        placeholderData: undefined,
    });

    if (isError) popup('error', error.message);

    return (
        <>
            <Modal
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                }}
                dialogClassName='overflow-hidden'
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title className='me-2'>{`Restore Documents`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={{
                            selectedFolder: undefined,
                        }}
                        onSubmit={async ({ selectedFolder }, { setSubmitting }) => {
                            setSubmitting(true);
                            const document_ids = selectedSafetyMeetings.map((item) => item.id);
                            const data = document_ids.map((id) => ({
                                id: id,
                                folder_id: selectedFolder.value,
                                deleted_at: null,
                            }));
                            try {
                                await updateMyDocumentsAPI({ data });
                                dispatch(setSelectedSafetyMeetings([]));
                                popup(
                                    'success',
                                    `Documents restored to folder "${selectedFolder.label}"!`,
                                );
                                setShowModal(false);
                                queryClient.invalidateQueries({
                                    queryKey: [QUERY_KEYS.MY_DOCUMENTS],
                                    exact: false,
                                });
                                queryClient.invalidateQueries({
                                    queryKey: [QUERY_KEYS.MY_DELETED_DOCUMENTS, accountId],
                                    exact: false,
                                });
                                queryClient.invalidateQueries({
                                    queryKey: [QUERY_KEYS.FOLDERS, accountId],
                                    exact: false,
                                });
                            } catch (error) {
                                popup('error', error.message);
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                    >
                        {({ values, isSubmitting, handleSubmit }) => (
                            <form action=''>
                                <div className='w-100'>
                                    <div className='d-flex justify-content-between position-relative'>
                                        <Field
                                            name='selectedFolder'
                                            label='Select Folders'
                                            options={folders}
                                            placeholder='Select a folder...'
                                            component={FormSelect}
                                            width={'100%'}
                                            isClearable={!!values.selectedFolder}
                                            onInputChange={setSearch}
                                            isSearchable
                                            isLoading={isFetching}
                                            col={12}
                                        />
                                    </div>

                                    <div className='divider mt-2 mb-3'></div>
                                    <div className='d-flex w-100 align-items-center justify-content-end modal-btn'>
                                        <Button
                                            type='submit'
                                            onClick={handleSubmit}
                                            className='table-footer-btn'
                                            disabled={!values.selectedFolder || isSubmitting}
                                        >
                                            {isSubmitting && (
                                                <span className='me-1'>
                                                    <CircleSpinner />
                                                </span>
                                            )}
                                            Restore
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </>
    );
};

RestoreDocumentModal.propTypes = {
    setShowModal: PropTypes.func.isRequired,
    showModal: PropTypes.bool.isRequired,
    getDeletedDocuments: PropTypes.func.isRequired,
};
